import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";
import bellhoptLogo from "../../icons/bellhoptLogoBlack.svg";

const faqItems = [
  {
    q: "Do I need API keys to connect Hospitable?",
    a: "No. Connection is completed through secure authorization in the Bellhopt Integrations screen.",
  },
  {
    q: "Do I need to set up webhooks?",
    a: "No webhook setup is required for the standard integration flow.",
  },
  {
    q: "Why do I need a Bellhopt account first?",
    a: "Bellhopt needs your host account record to securely save and map the Hospitable connection to the correct workspace.",
  },
  {
    q: "Is this the same as Hospitable's marketplace one-click CTA flow?",
    a: "Not exactly. The currently supported production flow is signup or login in Bellhopt first, then connect from the Integrations tab.",
  },
  {
    q: "How often should I sync?",
    a: "Run Sync Now after major property updates in Hospitable and during onboarding until your portfolio is fully aligned.",
  },
  {
    q: "Will Bellhopt create duplicate properties?",
    a: "The sync process checks for existing matches before creating new entries. It is built to update first and create only when needed.",
  },
  {
    q: "Why was a property skipped during sync?",
    a: "Most skips are caused by missing required location details, for example incomplete address fields or missing coordinates.",
  },
  {
    q: "What happens if I disconnect Hospitable?",
    a: "Bellhopt removes the active connection. You can reconnect any time from the Integrations tab.",
  },
  {
    q: "Will future properties in Hospitable require manual re-entry in Bellhopt?",
    a: "No manual re-entry should be needed in normal cases. Use Sync Now to bring new properties into Bellhopt.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function HospitableIntegrationPage() {
  const title = "Hospitable Integration | Connect Your PMS to Bellhopt Concierge";
  const description =
    "Connect Hospitable to Bellhopt to sync properties, automate pre-arrival grocery delivery, and run concierge operations without manual setup. 24-hour lead time required.";
  const canonical = "https://bellhopt.com/integrations/hospitable/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Bellhopt + Hospitable",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Integrations", item: "https://bellhopt.com/integrations/" },
        { "@type": "ListItem", position: 3, name: "Hospitable", item: canonical },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/integrations/hospitable/"
        robots="index,follow"
        ogTitle="Bellhopt + Hospitable"
        ogDescription="Connect Hospitable to Bellhopt to sync properties, automate pre-arrival grocery stocking, and run concierge operations at scale."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <div className="flex items-center justify-center gap-6 mobile:gap-4 mb-10 mobile:mb-8">
            <div className="flex items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white px-8 py-5 mobile:px-5 mobile:py-4 shadow-[0_4px_24px_rgba(15,23,42,0.06)]">
              <img
                src={bellhoptLogo}
                alt="Bellhopt"
                className="h-[48px] mobile:h-[36px] w-auto"
              />
            </div>
            <div className="flex items-center justify-center w-10 h-10 mobile:w-8 mobile:h-8 rounded-full border border-[#E5E7EB] bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
              <span className="text-[18px] mobile:text-[15px] font-medium text-[#6B7280] leading-none select-none">+</span>
            </div>
            <div className="flex items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white px-8 py-5 mobile:px-5 mobile:py-4 shadow-[0_4px_24px_rgba(15,23,42,0.06)]">
              <img
                src="https://bellhopt-public.s3.us-west-1.amazonaws.com/hospitable.svg"
                alt="Hospitable"
                className="h-[48px] mobile:h-[36px] w-auto"
              />
            </div>
          </div>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight text-center">
            Bellhopt + Hospitable
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8 text-center">
            Connect Hospitable to Bellhopt and keep your property data in sync so Bellhopt can run
            delivery logistics smoothly. Faster onboarding, less manual maintenance, and a stronger
            guest arrival standard.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Get Started With Bellhopt
            </a>
            <Link to="/integrations" className={SECONDARY_CTA}>
              Back To Integrations
            </Link>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="who" title="Who This Is For">
            <ul className="list-disc pl-6">
              <li>Property managers using Hospitable as their PMS</li>
              <li>Teams onboarding Bellhopt for guest grocery delivery and stocking operations</li>
              <li>Operators who want less manual setup and fewer recurring data updates</li>
            </ul>
          </Section>

          <Section id="prerequisites" title="What You Need Before Connecting">
            <ul className="list-disc pl-6">
              <li>An active Bellhopt host account</li>
              <li>An active Hospitable account with access to the properties you want to sync</li>
              <li>Permission to authorize third-party integrations in Hospitable</li>
            </ul>
          </Section>

          <Section id="how-to-connect" title="How to Connect Hospitable to Bellhopt">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Create your Bellhopt host account</strong>
                <p>
                  Go to{" "}
                  <a href="https://host.bellhopt.com/signup" className="text-primary underline">
                    host.bellhopt.com/signup
                  </a>{" "}
                  and sign up.
                </p>
              </li>
              <li>
                <strong>Log in to your Bellhopt dashboard</strong>
              </li>
              <li>
                <strong>Open the Integrations tab</strong>
              </li>
              <li>
                <strong>Find Hospitable Integration and click Connect</strong>
              </li>
              <li>
                <strong>Approve access in Hospitable</strong>
                <p>You will be redirected to Hospitable to authorize the connection.</p>
              </li>
              <li>
                <strong>Return to Bellhopt</strong>
                <p>After approval, you will see the integration marked as connected.</p>
              </li>
              <li>
                <strong>Click Sync Now to import and update your properties</strong>
              </li>
            </ol>
          </Section>

          <Section id="how-sync-works" title="How Sync Works">
            <p>When you click Sync Now, Bellhopt:</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Pulls property data from Hospitable</li>
              <li>Updates properties that already exist in Bellhopt</li>
              <li>Adds new properties that are not yet in Bellhopt when required location data is available</li>
              <li>Skips incomplete records and reports counts in the sync summary</li>
            </ul>
            <p className="mt-4">
              This keeps your Bellhopt property list aligned with Hospitable without repeated manual entry.
            </p>
          </Section>

          <Section id="data-used" title="Key Data Bellhopt Uses">
            <p>
              Bellhopt syncs and uses operationally relevant property details, including:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>Property identifiers and listing title</li>
              <li>Address and map coordinates</li>
              <li>Check-in and check-out timing fields</li>
              <li>Primary listing image and gallery references</li>
              <li>Guest access description from Hospitable details, when provided</li>
              <li>Additional metadata used for logistics and workflow context</li>
            </ul>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              What This Means for Your Team
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { title: "Faster onboarding", desc: "Skip manual property entry and get started in minutes." },
                { title: "Less maintenance", desc: "Keep property data current without re-entering details when listings change." },
                { title: "Better consistency", desc: "Operational data stays aligned so delivery logistics run smoothly." },
                { title: "Fewer missed updates", desc: "Sync whenever your Hospitable portfolio changes instead of tracking changes manually." },
              ].map((card) => (
                <div key={card.title} className="rounded-2xl border border-[#E5E7EB] p-5 bg-white/90">
                  <h3 className="font-segoe font-semibold text-[18px] text-[#111827]">{card.title}</h3>
                  <p className="mt-2 text-[15px] text-[#6B7280] leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Connect Hospitable and Start Delivering Arrival-Ready Stays
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Pre-arrival groceries, stocked and organized by Bellhopt Concierge. Sync your
              Hospitable properties and let Bellhopt handle the logistics.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
              <Link to="/for/property-managers" className={SECONDARY_CTA}>For Property Managers</Link>
            </div>
          </section>

          <Section id="important-notes" title="Important Notes">
            <ul className="list-disc pl-6">
              <li>You must sign up or log in to Bellhopt before connecting Hospitable.</li>
              <li>The connection is host-account scoped. Bellhopt stores the integration against your Bellhopt host record.</li>
              <li>If you add properties in Hospitable, run Sync Now in Bellhopt to pull the latest changes.</li>
              <li>If a Hospitable listing is missing key address or coordinate fields, Bellhopt may skip it until details are completed.</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <Section id="troubleshooting" title="Troubleshooting Checklist">
            <p>If connection or sync does not behave as expected:</p>
            <ol className="list-decimal pl-6 mt-4 space-y-2">
              <li>Confirm you are logged into the correct Bellhopt host account.</li>
              <li>Confirm Hospitable integration status is shown as connected.</li>
              <li>Run Sync Now again and review the success or error message.</li>
              <li>Verify skipped properties in Hospitable have complete address and coordinate details.</li>
              <li>Disconnect and reconnect Hospitable if authorization appears stale.</li>
              <li>Contact Bellhopt support with your host account email and approximate sync time.</li>
            </ol>
          </Section>

          <Section id="support" title="Support">
            <p>
              If your team needs help with setup validation, rollout sequencing, or post-sync review,
              contact Bellhopt support and include:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>Your Bellhopt host account email</li>
              <li>The time your last sync was run</li>
              <li>Example property names that did not sync as expected</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="mailto:support@bellhopt.com" className={SECONDARY_CTA}>Contact Support</a>
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Turn Hospitable Properties Into Arrival-Ready Stays
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Bellhopt is the concierge layer that plugs into Hospitable and executes pre-arrival
              grocery stocking and guest services without adding operational complexity.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/integrations">All integrations</Link></li>
              <li><Link className="text-primary" to="/integrations/streamline">Streamline integration</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/for/enterprise-property-managers">Enterprise property managers</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default HospitableIntegrationPage;
